import { createAsyncThunk } from '@reduxjs/toolkit';
import { dispatchRequest, getDispatchOrdersRequest, scanDispatchRequest } from './operatorDispatchApi';
import {
  LABEL_MATCH,
  MAX_LOAD_ORDERS,
  RECHECK,
  blockedMessage,
  buildDispatchBody,
  canLoadByTap,
  dispatchArgOf,
  dispatchOrderName,
  isDispatchBusy,
  loadOrderOutcome,
  loadSummary,
  recheckCodeOf,
  rowChanged,
  scanOutcome,
  shipmentRepeatLabel,
} from './operatorDispatchUtils';
import { OPERATOR_FLOW, OPERATOR_FLOW_LABELS } from '../operator/operatorConstants';
import { OPERATOR_ERROR, isRetryable, localError, readOperatorError } from '../operator/operatorErrors';
import { idempotentThunkOptions, sendWrite } from '../operator/operatorIdempotency';
import { SCAN_GATE, SCAN_SOURCE, cleanCode, scanGate } from '../operator/operatorScanGate';
import { offScreenMeta } from '../operator/operatorTaskScope';
import { selectCurrentOperatorWarehouseId } from '../operator/operatorUtils';

const warehouseOf = (getState) => selectCurrentOperatorWarehouseId(getState());

/** `{offScreenTask: 'Despacho'}` once the station is no longer on screen. */
const stationMeta = (getState) =>
  offScreenMeta(getState(), OPERATOR_FLOW.DISPATCH, null, OPERATOR_FLOW_LABELS[OPERATOR_FLOW.DISPATCH]);

/**
 * Why a label read at dispatch went nowhere. A 409 is an order that is not
 * for dispatch — still in packing, already gone, blocked —, said in the
 * server's words with its `reason` and `next_flow`: a warehouse rule, not a
 * clash to retry. A 404 is a code that is nothing here.
 *
 * @param {unknown} error
 * @param {string} code
 */
const readScanError = (error, code) => {
  const failure = readOperatorError(error, 'No pudimos leer ese código.');
  if (failure.status === 409) {
    const body = error?.response?.data || {};
    // A code of several orders (a shared reference): a label of one item says which.
    if (Array.isArray(body.candidates) && body.candidates.length > 0) {
      const names = body.candidates.map((order) => order.reference_id || `#${order.order_id}`).join(', ');
      return {
        ...localError(`${code} es de varias órdenes (${names}): escanea la etiqueta de un bulto.`),
        kind: OPERATOR_ERROR.REFUSED,
        reason: 'ambiguous',
      };
    }
    return {
      ...readOperatorError(error, 'Esa orden no está para despacho.'),
      kind: OPERATOR_ERROR.REFUSED,
      reason: body.reason ?? null,
      nextFlow: body.next_flow ?? null,
    };
  }
  if (failure.kind === OPERATOR_ERROR.NOT_FOUND) return { ...failure, message: `${code} no es un bulto de esta bodega.` };
  return failure;
};

/** An order the scan found blocked, said as the server would. */
const blockedFailure = (row) => ({ ...localError(blockedMessage(row)), kind: OPERATOR_ERROR.REFUSED, reason: 'blocked' });

/** The orders ready to leave, a page at a time, of one courier or all. Page 1 replaces, the rest append. */
export const fetchDispatchQueue = createAsyncThunk(
  'operatorDispatch/fetchQueue',
  async ({ courier = '', page = 1 } = {}, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await getDispatchOrdersRequest(warehouseOf(getState), { courier, page });
      return fulfillWithValue(data, { receivedAt: Date.now() });
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos cargar las órdenes por despachar.'));
    }
  }
);

/**
 * A label read at the station. The server says which order and item it is;
 * what it does to the load — an item up, the shipment counted once more,
 * nothing — is decided here against the load as it stands (`scanOutcome`), and
 * the slice takes it. An order that is not for dispatch, or blocked, never
 * goes up: the band says why. The read lands in the load even once the
 * station is off screen: the item was scanned.
 *
 * @param {{code: string, source: string}} read
 */
export const scanDispatchCode = createAsyncThunk(
  'operatorDispatch/scanCode',
  async ({ code, source }, { getState, fulfillWithValue, rejectWithValue }) => {
    const value = cleanCode(code);
    const warehouseId = warehouseOf(getState);
    try {
      const { data } = await scanDispatchRequest(warehouseId, value);
      if (!data?.order) return rejectWithValue(localError(`${value} no es un bulto de esta bodega.`), stationMeta(getState));
      if (data.order.dispatchable === false) return rejectWithValue(blockedFailure(data.order), stationMeta(getState));
      const { orders } = getState().operatorDispatch.load;
      const entry = orders[data.order.id];
      if (!entry && Object.keys(orders).length >= MAX_LOAD_ORDERS) {
        return rejectWithValue(
          localError(`Una carga lleva hasta ${MAX_LOAD_ORDERS} órdenes: despacha las completas antes de seguir.`),
          stationMeta(getState)
        );
      }
      return fulfillWithValue(
        { code: value, warehouseId, outcome: scanOutcome(entry, data, { code: value, source }) },
        stationMeta(getState)
      );
    } catch (error) {
      return rejectWithValue(readScanError(error, value), stationMeta(getState));
    }
  },
  {
    condition: (read, { getState }) => {
      const dispatchState = getState().operatorDispatch;
      return (
        scanGate({ busy: isDispatchBusy(dispatchState), lastCode: dispatchState.scan.lastCode }, read) ===
        SCAN_GATE.ACCEPT
      );
    },
  }
);

/**
 * An order tapped in the queue, once confirmed: every item of it up the load
 * without scanning them. It is read again first, by its first item's id as
 * the big QR of its label is, so an order blocked, gone or changed since the
 * queue showed it says so as a scan would. Nothing is sent: it leaves with
 * the dispatch.
 *
 * @param {{orderId: number}} arg
 */
export const loadDispatchOrder = createAsyncThunk(
  'operatorDispatch/loadOrder',
  async ({ orderId }, { getState, fulfillWithValue, rejectWithValue }) => {
    const warehouseId = warehouseOf(getState);
    const row = getState().operatorDispatch.queue.items.find((item) => item.id === orderId);
    const code = String(row.items[0].id);
    const gone = () => localError(`${dispatchOrderName(row)} ya no está para despacho: actualiza la cola.`);
    try {
      const { data } = await scanDispatchRequest(warehouseId, code);
      if (data?.order?.id !== orderId) return rejectWithValue(gone(), stationMeta(getState));
      if (data.order.dispatchable === false) return rejectWithValue(blockedFailure(data.order), stationMeta(getState));
      const { orders } = getState().operatorDispatch.load;
      const entry = orders[orderId];
      if (!entry && Object.keys(orders).length >= MAX_LOAD_ORDERS) {
        return rejectWithValue(
          localError(`Una carga lleva hasta ${MAX_LOAD_ORDERS} órdenes: despacha las completas antes de seguir.`),
          stationMeta(getState)
        );
      }
      return fulfillWithValue({ warehouseId, outcome: loadOrderOutcome(entry, data.order, code) }, stationMeta(getState));
    } catch (error) {
      const failure = readScanError(error, code);
      return rejectWithValue(failure.kind === OPERATOR_ERROR.NOT_FOUND ? gone() : failure, stationMeta(getState));
    }
  },
  {
    condition: ({ orderId }, { getState }) => {
      const dispatchState = getState().operatorDispatch;
      const row = dispatchState.queue.items.find((item) => item.id === orderId);
      return !isDispatchBusy(dispatchState) && canLoadByTap(row, dispatchState.load.orders[orderId]);
    },
  }
);

/** «+1 bulto»: the shipment label of the order last read, once more; it counts one more item. */
export const repeatShipmentItem = () => (dispatch, getState) => {
  const { lastShipment, load } = getState().operatorDispatch;
  if (!lastShipment || !shipmentRepeatLabel(load.orders[lastShipment.orderId])) return null;
  return dispatch(scanDispatchCode({ code: lastShipment.code, source: SCAN_SOURCE.REPEAT }));
};

/**
 * Dispatches the complete orders of the load, under a key. Each order
 * answers on its own: one that left — now or by an earlier try whose answer
 * was lost — leaves the load; one that failed stays, marked with why.
 *
 * @param {{orders: Array<{orderId: number, itemIds?: number[], itemsCount?: number}>, idempotencyKey?: string}} arg
 */
export const dispatchLoad = createAsyncThunk(
  'operatorDispatch/dispatch',
  async (arg, { getState, requestId, fulfillWithValue, rejectWithValue }) => {
    try {
      const { data } = await dispatchRequest(warehouseOf(getState), buildDispatchBody(arg, requestId));
      return fulfillWithValue(data, stationMeta(getState));
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos despachar la carga.'), stationMeta(getState));
    }
  },
  {
    ...idempotentThunkOptions,
    condition: (arg, thunkApi) => arg?.orders?.length > 0 && idempotentThunkOptions.condition(arg, thunkApi),
  }
);

/**
 * The primary, once confirmed: the complete orders of the load. The same
 * load sent again after a lost answer goes under the key it was first sent
 * with, so the server dispatches it once.
 */
export const submitDispatch = () => (dispatch, getState) => {
  const summary = loadSummary(getState().operatorDispatch.load);
  if (summary.complete.length === 0) return null;
  return dispatch(sendWrite(dispatchLoad, dispatchArgOf(summary), (state) => state.operatorDispatch));
};

/** A failed order read again with its label: how it stands now. */
const recheck = async (warehouseId, entry) => {
  const orderId = entry.row.id;
  const name = dispatchOrderName(entry.row);
  const code = recheckCodeOf(entry);
  if (!code) return { orderId, name, status: RECHECK.RESCAN, row: entry.row };
  try {
    const { data } = await scanDispatchRequest(warehouseId, code);
    const row = data?.order;
    if (!row || row.id !== orderId) {
      return { orderId, name, status: RECHECK.REFUSED, message: 'su etiqueta ya no la encuentra. Sácala de la carga' };
    }
    if (row.dispatchable === false) {
      const reason = row.blocked_reason || 'está bloqueada';
      return { orderId, name, status: RECHECK.REFUSED, row, message: `no se puede despachar: ${reason}` };
    }
    return { orderId, name, status: rowChanged(entry.row, row) ? RECHECK.RESCAN : RECHECK.READY, row };
  } catch (error) {
    const failure = readScanError(error, code);
    if (isRetryable(failure)) return { orderId, name, status: RECHECK.LOST, message: failure.message };
    // The item it was read by is gone, packed again: its items are scanned over, however many it has now.
    if (entry.match === LABEL_MATCH.ITEM && failure.kind === OPERATOR_ERROR.NOT_FOUND) {
      return { orderId, name, status: RECHECK.RESCAN, row: entry.row, itemGone: true };
    }
    return { orderId, name, status: RECHECK.REFUSED, message: failure.message };
  }
};

/**
 * «Reintentar las que fallaron». The server says why an order failed in
 * words, not which kind of failure it was, so resending them as they are
 * would fail again the ones whose items changed. Instead each failed order is
 * read again with its label: one unchanged and still for dispatch is complete
 * again, and goes once the operator confirms it; one whose items changed
 * starts its scans over, as the server asks; one the server refuses now
 * stays marked, to take out of the load. Nothing is sent from here: every
 * dispatch goes through the confirmation sheet.
 */
export const retryFailedDispatch = createAsyncThunk(
  'operatorDispatch/retryFailed',
  async (_, { getState }) => {
    const { failed } = loadSummary(getState().operatorDispatch.load);
    const warehouseId = warehouseOf(getState);
    const checks = await Promise.all(failed.map((entry) => recheck(warehouseId, entry)));
    return { checks };
  },
  {
    condition: (_, { getState }) => {
      const dispatchState = getState().operatorDispatch;
      return !isDispatchBusy(dispatchState) && loadSummary(dispatchState.load).failed.length > 0;
    },
  }
);
